import { useQuery } from '@tanstack/react-query';
import { PkCard, PkCarousel } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo, useMemo } from 'react';

import { useArticleLinkRule } from '@hooks/useLinkRule';
import { getWebContainerQuery } from '@queries/editorial';
import { trackCardElement } from '@services/GoogleTagManagerService';
import ErrorBoundary, { withErrorBoundary } from '@shared/ErrorBoundary';
import { useTranslateFn } from '@shared/Translate';
import { competitionCode } from '@utilities/WindowContext';

import * as styles from './styles.module.scss';

const breakpoints = {
  0: {
    slidesPerView: '1.1',
    slidesPerGroup: 1,
    spaceBetween: 12,
  },
  768: {
    slidesPerView: '2.2',
    slidesPerGroup: 3,
    spaceBetween: 12,
  },
  1024: {
    slidesPerView: '3.2',
    slidesPerGroup: 3,
    spaceBetween: 12,
  },
  1200: {
    slidesPerView: '3.3',
    slidesPerGroup: 4,
    spaceBetween: 24,
  },
};

const WEB_CONTAINER = `${competitionCode}.TICKETS.INFO`.toUpperCase();

const TicketsInfo = () => {
  const t = useTranslateFn();
  const { data: articles } = useQuery(getWebContainerQuery(WEB_CONTAINER));

  if (!articles?.length) return null;

  return (
    <div className={clsx(styles.wrapper, 'pk-container', 'pk-bg--primary-01')} pk-theme='dark'>
      <div className='pk-col pk-col--span-12'>
        <ErrorBoundary>
          <PkCarousel
            key='info-carousel'
            className={clsx('js-tracking-container', styles.pkCarousel)}
            boxShadowPadding
            breakpoints={JSON.stringify(breakpoints)}
          >
            <p slot='headline'>{t('how_can_get_tickets')}</p>
            {articles.map((article) => (
              <Card key={article.id} article={article} />
            ))}
          </PkCarousel>
        </ErrorBoundary>
      </div>
    </div>
  );
};

const Card = ({ article }) => {
  const href = useArticleLinkRule(article.id);

  const trackingData = useMemo(() => {
    const name = article.title;
    const category = 'article card';
    const id = article.id;

    return {
      name,
      id,
      category,
    };
  }, [article.id, article.title]);

  return (
    <div>
      <a href={`${href}`} title={article.title} className={styles.articleCardLink} ref={trackCardElement(trackingData)}>
        <PkCard className={styles.articleCard}>
          <div slot='heading'>
            <img className={styles.img} src={article.imageUrl} title={article.title} alt={article.title} />
          </div>
          <div slot='body'>
            <div className={clsx(styles.articleCardTitle, 'pk-font--base-bold pk-text--text-01')}>{article.title}</div>
            <div className={clsx(styles.articleCardDescription, 'pk-m--0 pk-text--text-01 pk-mt--s')}>
              {article.description}
            </div>
          </div>
        </PkCard>
      </a>
    </div>
  );
};

export default withErrorBoundary(memo(TicketsInfo));
